import { useState, useEffect, lazy as reactLazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { clearBookingCache } from "@/lib/bookingCache";
import { HelmetProvider } from "react-helmet-async";
import { HashRouter, Routes, Route, Navigate, useLocation, useMatch, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Capacitor } from "@capacitor/core";

// Componente para redirecionamento com parâmetros
const ReservaRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/reservas/${id}`} replace />;
};


import MobileBottomTabs from "@/components/layout/MobileBottomTabs";
import PageTransition from "@/components/layout/PageTransition";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import { useAuth } from "@/contexts/AuthContext";
import { OptimizedSplash } from "@/components/layout/OptimizedSplash";
import { useGlobalGeolocation } from "@/hooks/useGlobalGeolocation";
import { checkAndClearCache } from "@/lib/cacheManager";
import { registerSW } from "@/lib/swRegister";
import { exposeCacheResetGlobal } from "@/lib/cacheReset";
import { OfflineIndicator, SyncStatus } from "@/components/ui/OfflineIndicator";
import { InstallPWAPrompt, UpdatePrompt } from "@/components/ui/InstallPWA";
import { useOffline } from "@/hooks/useOffline";
import TrackingPixels from "./components/layout/TrackingPixels";
import { ErrorBoundary } from "@/components/layout/ErrorBoundary";
import { useNativeBackButton } from "@/hooks/useNativeBackButton";
import { useMobileKeyboard } from "@/hooks/useMobileKeyboard";

import { PushNotificationManager } from "@/components/notifications/PushNotificationManager";
import { useUnifiedSync } from "@/hooks/useUnifiedSync";
import { preloadMapboxToken } from '@/components/PropertyMapOptimized';
import { shouldShowOnboarding } from "@/hooks/usePermissions";
import BeraIAWidget from "@/components/bera-ai/BeraIAWidget";
import PostLoginVerifyGate from "@/components/auth/PostLoginVerifyGate";
import TwoFactorGate from "@/components/security/TwoFactorGate";
import MaintenanceGate from "@/components/system/MaintenanceGate";
import { useShareIntent } from "@/hooks/useShareIntent";
import GlobalPaymentConfirmedGate from "@/components/payments/GlobalPaymentConfirmedGate";

import { UpdateBanner } from "@/components/UpdateBanner";
import { useLanguagePersistence } from "@/hooks/useLanguagePersistence";
import { useDynamicTranslations } from "@/hooks/useDynamicTranslations";

// Custom lazy wrapper to handle Vite chunk loading errors on new deployments
const lazy = (componentImport: () => Promise<any>) => {
  return reactLazy(async () => {
    try {
      const component = await componentImport();
      sessionStorage.removeItem("chunk_reload");
      return component;
    } catch (error) {
      console.warn("Chunk load error (Vite dynamically imported module), reloading...", error);
      const isReloaded = sessionStorage.getItem("chunk_reload");
      if (!isReloaded) {
        sessionStorage.setItem("chunk_reload", "true");
        window.location.reload();
      }
      return { default: () => (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 text-center">
          <div className="w-8 h-8 border-4 border-[#E8513D] border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-muted-foreground font-medium">{typeof window !== "undefined" && window.localStorage.getItem("i18nextLng")?.startsWith("en") ? "Updating app..." : "A atualizar a aplicação..."}</p>
        </div>
      ) };
    }
  });
};

// Eagerly load critical pages for instant first render
import Index from "./pages/Index";
import Explorar from "./pages/Explorar";

// Lazy load non-critical pages for better performance
const Auth = lazy(() => import("./pages/Auth"));
const PropertyDetails = lazy(() => import("./pages/PropertyDetails"));
const HostDashboard = lazy(() => import("./pages/HostDashboard"));
const HostProperties = lazy(() => import("./pages/HostProperties"));
const HostBookings = lazy(() => import("./pages/HostBookings"));
const HostCalendar = lazy(() => import("./pages/HostCalendar"));
const HostFinancials = lazy(() => import("./pages/HostFinancials"));
const AddProperty = lazy(() => import("./pages/AddProperty"));
const Favoritos = lazy(() => import("./pages/Favoritos"));
const Perfil = lazy(() => import("./pages/Perfil"));
const Configuracoes = lazy(() => import("./pages/Configuracoes"));
const MinhasReservas = lazy(() => import("./pages/MinhasReservas"));
const BookingDetails = lazy(() => import("./pages/BookingDetails"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Incompativel = lazy(() => import("./pages/Incompativel"));
const Termos = lazy(() => import("./pages/Termos"));
const OpcoesCancelamento = lazy(() => import("./pages/OpcoesCancelamento"));
const Ajuda = lazy(() => import("./pages/Ajuda"));
const Privacidade = lazy(() => import("./pages/Privacidade"));

const FazerOferta = lazy(() => import("./pages/FazerOferta"));
const OfertasRecebidas = lazy(() => import("./pages/OfertasRecebidas"));
const Notificacoes = lazy(() => import("./pages/Notificacoes"));
const Suporte = lazy(() => import("./pages/Suporte"));

// Insurance Module - lazy loaded
const MeuSeguro = lazy(() => import("./pages/MeuSeguro"));
const MeuSeguroDetalhe = lazy(() => import("./pages/MeuSeguroDetalhe"));
const InsurerLogin = lazy(() => import("./pages/insurer/InsurerLogin"));
const InsurerLayout = lazy(() => import("./pages/insurer/InsurerLayout"));
const InsurerRequests = lazy(() => import("./pages/insurer/InsurerRequests"));
const InsurerRequestDetail = lazy(() => import("./pages/insurer/InsurerRequestDetail"));
const AdminInsurance = lazy(() => import("./pages/admin/AdminInsurance"));
const AdminInsuranceCompanies = lazy(() => import("./pages/admin/AdminInsuranceCompanies"));

// Affiliate Pages - lazy loaded
const AfiliadoAuth = lazy(() => import("./pages/AfiliadoAuth"));
const Onboarding = lazy(() => import("./components/onboarding/UnifiedOnboarding"));
const AfiliatePanel = lazy(() => import("./pages/AfiliatePanel"));
const AffiliateDashboard = lazy(() => import("./pages/affiliate/AffiliateDashboard"));
const AffiliateExplore = lazy(() => import("./pages/affiliate/AffiliateExplore"));
const AffiliateReservations = lazy(() => import("./pages/affiliate/AffiliateReservations"));
const AffiliateTransactions = lazy(() => import("./pages/affiliate/AffiliateTransactions"));
const AffiliateBalance = lazy(() => import("./pages/affiliate/AffiliateBalance"));
const AffiliateNotifications = lazy(() => import("./pages/affiliate/AffiliateNotifications"));
const AffiliateProfile = lazy(() => import("./pages/affiliate/AffiliateProfile"));
const AffiliateRedirect = lazy(() => import("./pages/AffiliateRedirect"));

// Admin Pages - lazy loaded  
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminProperties = lazy(() => import("./pages/admin/AdminProperties"));
const AdminKYC = lazy(() => import("./pages/admin/AdminKYC"));
const AdminKycPrint = lazy(() => import("./pages/admin/AdminKycPrint"));
const AdminBookings = lazy(() => import("./pages/admin/AdminBookings"));
const AdminFinancials = lazy(() => import("./pages/admin/AdminFinancials"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));
const AdminAffiliates = lazy(() => import("./pages/admin/AdminAffiliates"));
const AdminOffers = lazy(() => import("./pages/admin/AdminOffers"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminChat = lazy(() => import("./pages/admin/AdminChat"));
const AdminNotifications = lazy(() => import("./pages/admin/AdminNotifications"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminAudit = lazy(() => import("./pages/admin/AdminAudit"));
const AdminTranslations = lazy(() => import("./pages/admin/AdminTranslations"));
const AdminWithdrawals = lazy(() => import("./pages/admin/AdminWithdrawals"));
const AdminPartners = lazy(() => import("./pages/admin/AdminPartners"));
const AdminTransactions = lazy(() => import("./pages/admin/AdminTransactions"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminBeraAI = lazy(() => import("./pages/admin/AdminBeraAI"));
const AdminDisputes = lazy(() => import("./pages/admin/AdminDisputes"));
const AdminRefunds = lazy(() => import("./pages/admin/AdminRefunds"));

const AdminDisputeDetail = lazy(() => import("./pages/admin/AdminDisputeDetail"));

// Ultra-minimal loader - matches brand background on native to prevent black/white flash
const PageLoader = () => {
  const isAndroidNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
  const isNative = Capacitor.isNativePlatform();

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[9998] bg-[#FAF6F1]"
    >
      {isAndroidNative ? (
        <div className="w-10 h-10 rounded-xl bg-[#E8513D]/10 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-[#E8513D] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.1 }}
          className="w-10 h-10 rounded-xl bg-[#E8513D]/10 flex items-center justify-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-5 h-5 border-2 border-[#E8513D] border-t-transparent rounded-full"
          />
        </motion.div>
      )}
    </div>
  );
};

// Delayed fallback hook - prevents flash on fast navigations
const useDelayedFallback = (delay = 200) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return show;
};

const DelayedPageLoader = () => {
  const show = useDelayedFallback(200);
  return show ? <PageLoader /> : null;
};

// Wrap lazy components with optimized loading - delayed fallback
const OpenAppRedirect = lazy(() => import('@/components/shared/OpenAppRedirect'));
const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<DelayedPageLoader />}>
    {children}
  </Suspense>
);

const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <PageLoader />;
  if (!user) {
    return (
      <Navigate
        to="/auth?mode=login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
  const isNative = Capacitor.isNativePlatform();
  if (isNative && !isDesktop && location.pathname !== "/onboarding" && shouldShowOnboarding()) {
    const from = `${location.pathname}${location.search}`;
    return <Navigate to="/onboarding" replace state={{ from }} />;
  }
  return <>{children}</>;
};

// PublicOnlyRoute - Redirects authenticated users away from public pages (like landing page)
const PublicOnlyRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <PageLoader />;
  
  // If user is logged in, redirect to /explorar (replace state to prevent back navigation)
  if (user) {
    // Check if user came from logout - allow landing page only in this case
    const fromLogout = location.state?.fromLogout === true;
    if (fromLogout) {
      return <>{children}</>;
    }
    return <Navigate to="/explorar" replace />;
  }
  
  return <>{children}</>;
};

const AndroidIndexRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
  const isNative = Capacitor.isNativePlatform();
  if (user && isNative && !isDesktop && shouldShowOnboarding()) {
    return <Navigate to="/onboarding" replace state={{ from: "/explorar" }} />;
  }
  return <Navigate to={user ? "/explorar" : "/auth?mode=login"} replace />;
};

/**
 * QueryClient otimizado para performance
 * 
 * Estratégia de cache:
 * - staleTime: tempo antes de considerar dados desatualizados
 * - gcTime: tempo mantendo dados em cache após último uso
 * - Refetch automático desabilitado em background para economizar bateria/dados
 *
 * QueryClient está definido em @/lib/queryClient.ts para evitar
 * dependência circular com AuthContext.
 */

// Debug logging for Android
const DEBUG = false;
const log = (...args: any[]) => DEBUG && console.log('[BeraStays]', ...args);

/**
 * Check if running in Capacitor native app
 */
const isCapacitorNative = (): boolean => {
  return typeof (window as any).Capacitor !== 'undefined' && 
         (window as any).Capacitor.isNativePlatform?.() === true;
};

// Inner app content that requires Router context
const AppContent = () => {
  // Route matching and location - MUST be inside Router context
  const location = useLocation();
  const isOnboardingRoute = !!useMatch('/onboarding');
  const isAuthRoute = !!useMatch('/auth');
  const isAdminRoute = !!useMatch('/admin/*');
  const isInsurerRoute = !!useMatch('/seguradora/*') || location.pathname === '/seguradora/login';
  const isSuporteRoute = !!useMatch('/suporte');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Listen for search bar focus changes to hide bottom tabs
  useEffect(() => {
    const handler = (e: CustomEvent) => setIsSearchFocused(e.detail.focused);
    window.addEventListener('search-focus-change', handler as EventListener);
    return () => window.removeEventListener('search-focus-change', handler as EventListener);
  }, []);

  const isPropertyHostPreview = location.pathname.startsWith('/imovel/') && new URLSearchParams(location.search).get('preview') === 'host';
  const showBottomTabs = !isOnboardingRoute && !isAuthRoute && !isAdminRoute && !isInsurerRoute && !isSuporteRoute && !isSearchFocused && !isPropertyHostPreview;
  
  // Hooks that depend on Router
  useNativeBackButton();

  // Global mobile keyboard awareness (visualViewport + focus scroll)
  useMobileKeyboard();

  const isAndroidNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";


  // Show React splash screen only on native platforms (skip on desktop web for instant landing page)
  const isNativePlatform = Capacitor.isNativePlatform();
  const [showSplash, setShowSplash] = useState(isNativePlatform);
  const [appBooted, setAppBooted] = useState(false);

  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    let resizeTimer: ReturnType<typeof setTimeout>;
    
    const handleResize = () => {
      setIsResizing(true);
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setIsResizing(false);
      }, 300);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  // Guarda de pintura otimizado: overlay curto para transição suave
  useEffect(() => {
    if (!showSplash && isNativePlatform) {
      const t = setTimeout(() => setAppBooted(true), 300);
      return () => clearTimeout(t);
    }
  }, [showSplash, isNativePlatform]);

  // Prefetch rotas críticas no idle para acelerar troca de páginas em mobile/native
  useEffect(() => {
    const prefetch = () => {
      // Importações lazy disparadas em background — Vite faz cache do chunk
      import("./pages/MinhasReservas").catch(() => {});
      import("./pages/Notificacoes").catch(() => {});
      import("./pages/Perfil").catch(() => {});
      import("./pages/FazerOferta").catch(() => {});
      import("./pages/PropertyDetails").catch(() => {});
      import("./pages/Favoritos").catch(() => {});
    };

    if ("requestIdleCallback" in window) {
      const id = (window as any).requestIdleCallback(prefetch, { timeout: 3000 });
      return () => (window as any).cancelIdleCallback?.(id);
    }
    const t = setTimeout(prefetch, 1500);
    return () => clearTimeout(t);
  }, []);

  const ShareIntentBridge = () => {
    useShareIntent();
    return null;
  };

  const LanguagePersistence = () => {
    useLanguagePersistence();
    useDynamicTranslations();
    return null;
  };

  return (
    <AuthProvider>
      <LanguagePersistence />
      <ShareIntentBridge />
      <PushNotificationManager />
      <div className={`viewport-resize-transition ${isResizing ? 'is-resizing' : ''}`}>
        {showSplash && isNativePlatform && (
          <OptimizedSplash onComplete={() => setShowSplash(false)} />
        )}
        {!appBooted && isNativePlatform && (
          <motion.div
            className="fixed inset-0 z-[99998] bg-transparent"
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ pointerEvents: "none" }}
          />
        )}
        {(!showSplash || !isNativePlatform) && (
          <>
            {isAndroidNative ? (
              <Routes>
                  {/* Core routes - eagerly loaded */}
                  <Route path="/" element={<AndroidIndexRedirect />} />
                  <Route path="/open" element={<LazyWrapper><OpenAppRedirect /></LazyWrapper>} />
                  <Route path="/app" element={<LazyWrapper><OpenAppRedirect /></LazyWrapper>} />
                  <Route path="/explorar" element={<RequireAuth><PageTransition><Explorar /></PageTransition></RequireAuth>} />
                  
                  {/* Onboarding - for first-time native app users */}
                  <Route path="/onboarding" element={<RequireAuth><PageTransition><LazyWrapper><Onboarding /></LazyWrapper></PageTransition></RequireAuth>} />
                  
                  {/* Lazy loaded routes - wrapped with LazyWrapper */}
                  <Route path="/auth" element={<PageTransition><LazyWrapper><Auth /></LazyWrapper></PageTransition>} />
                  <Route path="/imovel/:id" element={<PageTransition><LazyWrapper><PropertyDetails /></LazyWrapper></PageTransition>} />
                  <Route path="/dashboard" element={<Navigate to="/dashboard/anuncios" replace />} />
                  <Route path="/dashboard/anuncios" element={<RequireAuth><PageTransition><LazyWrapper><HostProperties /></LazyWrapper></PageTransition></RequireAuth>} />
                  <Route path="/dashboard/reservas" element={<RequireAuth><PageTransition><LazyWrapper><HostBookings /></LazyWrapper></PageTransition></RequireAuth>} />
                  <Route path="/dashboard/calendario" element={<RequireAuth><PageTransition><LazyWrapper><HostCalendar /></LazyWrapper></PageTransition></RequireAuth>} />
                  <Route path="/dashboard/financeiro" element={<RequireAuth><PageTransition><LazyWrapper><HostFinancials /></LazyWrapper></PageTransition></RequireAuth>} />
                  <Route path="/anunciar" element={<RequireAuth><PageTransition><LazyWrapper><AddProperty /></LazyWrapper></PageTransition></RequireAuth>} />
                  <Route path="/favoritos" element={<RequireAuth><PageTransition><LazyWrapper><Favoritos /></LazyWrapper></PageTransition></RequireAuth>} />
                  <Route path="/perfil" element={<RequireAuth><PageTransition><LazyWrapper><Perfil /></LazyWrapper></PageTransition></RequireAuth>} />
                  <Route path="/configuracoes" element={<RequireAuth><PageTransition><LazyWrapper><Configuracoes /></LazyWrapper></PageTransition></RequireAuth>} />
                  <Route path="/suporte" element={<RequireAuth><LazyWrapper><Suporte /></LazyWrapper></RequireAuth>} />
                  {/* Reservas - URLs modernizadas */}
                  <Route path="/reservas" element={<RequireAuth><PageTransition><LazyWrapper><MinhasReservas /></LazyWrapper></PageTransition></RequireAuth>} />
                  <Route path="/reservas/:id" element={<RequireAuth><PageTransition><LazyWrapper><BookingDetails /></LazyWrapper></PageTransition></RequireAuth>} />
                  {/* ReservaConfirmada removed — success is shown inline in PaymentFlow modal */}
                  
                  {/* Ofertas - estrutura hierárquica */}
                  <Route path="/ofertas/nova" element={<RequireAuth><PageTransition><LazyWrapper><FazerOferta /></LazyWrapper></PageTransition></RequireAuth>} />
                  <Route path="/ofertas/recebidas" element={<RequireAuth><PageTransition><LazyWrapper><OfertasRecebidas /></LazyWrapper></PageTransition></RequireAuth>} />
                  
                  {/* Redirects para URLs antigas (backward compatibility) */}
                  <Route path="/minhas-reservas" element={<Navigate to="/reservas" replace />} />
                  <Route path="/reserva/:id" element={<ReservaRedirect />} />
                  <Route path="/reserva-confirmada/:id" element={<ReservaRedirect />} />
                  <Route path="/fazer-oferta" element={<Navigate to="/ofertas/nova" replace />} />
                  <Route path="/ofertas-recebidas" element={<Navigate to="/ofertas/recebidas" replace />} />
                  <Route path="/notificacoes" element={<RequireAuth><PageTransition><LazyWrapper><Notificacoes /></LazyWrapper></PageTransition></RequireAuth>} />

                  {/* Insurance — Owner */}
                  <Route path="/seguro" element={<RequireAuth><PageTransition><LazyWrapper><MeuSeguro /></LazyWrapper></PageTransition></RequireAuth>} />
                  <Route path="/seguro/imovel/:requestId" element={<RequireAuth><PageTransition><LazyWrapper><MeuSeguroDetalhe /></LazyWrapper></PageTransition></RequireAuth>} />

                  {/* Insurance — Insurer (own auth, no RequireAuth) */}
                  <Route path="/seguradora/login" element={<PageTransition><LazyWrapper><InsurerLogin /></LazyWrapper></PageTransition>} />
                  <Route path="/seguradora" element={<PageTransition><LazyWrapper><InsurerLayout /></LazyWrapper></PageTransition>}>
                    <Route index element={<LazyWrapper><InsurerRequests /></LazyWrapper>} />
                    <Route path="pedidos" element={<LazyWrapper><InsurerRequests /></LazyWrapper>} />
                    <Route path="pedidos/:id" element={<LazyWrapper><InsurerRequestDetail /></LazyWrapper>} />
                  </Route>
                  <Route path="/termos" element={<RequireAuth><PageTransition><LazyWrapper><Termos /></LazyWrapper></PageTransition></RequireAuth>} />
                  <Route path="/opcoes-de-cancelamento" element={<RequireAuth><PageTransition><LazyWrapper><OpcoesCancelamento /></LazyWrapper></PageTransition></RequireAuth>} />
                  <Route path="/ajuda" element={<RequireAuth><PageTransition><LazyWrapper><Ajuda /></LazyWrapper></PageTransition></RequireAuth>} />
                  <Route path="/privacidade" element={<RequireAuth><PageTransition><LazyWrapper><Privacidade /></LazyWrapper></PageTransition></RequireAuth>} />
                
                {/* Affiliate Routes - lazy loaded */}
                <Route path="/afiliados" element={<LazyWrapper><AfiliadoAuth /></LazyWrapper>} />
                <Route path="/afiliados/painel" element={<RequireAuth><LazyWrapper><AfiliatePanel /></LazyWrapper></RequireAuth>}>
                  <Route index element={<LazyWrapper><AffiliateDashboard /></LazyWrapper>} />
                  <Route path="explorar" element={<LazyWrapper><AffiliateExplore /></LazyWrapper>} />
                  <Route path="reservas" element={<LazyWrapper><AffiliateReservations /></LazyWrapper>} />
                  <Route path="transacoes" element={<LazyWrapper><AffiliateTransactions /></LazyWrapper>} />
                  <Route path="saldo" element={<LazyWrapper><AffiliateBalance /></LazyWrapper>} />
                  <Route path="notificacoes" element={<LazyWrapper><AffiliateNotifications /></LazyWrapper>} />
                  <Route path="perfil" element={<LazyWrapper><AffiliateProfile /></LazyWrapper>} />
                </Route>
                <Route path="/ref/:code" element={<LazyWrapper><AffiliateRedirect /></LazyWrapper>} />
                
                {/* Admin Routes - lazy loaded */}
                <Route path="/admin/login" element={<LazyWrapper><AdminLogin /></LazyWrapper>} />
                <Route path="/admin/kyc/print/:userId" element={<RequireAuth><LazyWrapper><AdminKycPrint /></LazyWrapper></RequireAuth>} />
                <Route path="/admin" element={<RequireAuth><LazyWrapper><AdminLayout /></LazyWrapper></RequireAuth>}>
                  <Route index element={<LazyWrapper><AdminDashboard /></LazyWrapper>} />
                  <Route path="utilizadores" element={<LazyWrapper><AdminUsers /></LazyWrapper>} />
                  <Route path="imoveis" element={<LazyWrapper><AdminProperties /></LazyWrapper>} />
                  <Route path="kyc" element={<LazyWrapper><AdminKYC /></LazyWrapper>} />
                  <Route path="reservas" element={<LazyWrapper><AdminBookings /></LazyWrapper>} />
                  <Route path="financeiro" element={<LazyWrapper><AdminFinancials /></LazyWrapper>} />
                  <Route path="pagamentos" element={<LazyWrapper><AdminPayments /></LazyWrapper>} />
                  <Route path="transacoes" element={<LazyWrapper><AdminTransactions /></LazyWrapper>} />
                  <Route path="afiliados" element={<LazyWrapper><AdminAffiliates /></LazyWrapper>} />
                  <Route path="ofertas" element={<LazyWrapper><AdminOffers /></LazyWrapper>} />
                  <Route path="avaliacoes" element={<LazyWrapper><AdminReviews /></LazyWrapper>} />
                  <Route path="chat" element={<LazyWrapper><AdminChat /></LazyWrapper>} />
                  <Route path="notificacoes" element={<LazyWrapper><AdminNotifications /></LazyWrapper>} />
                  <Route path="auditoria" element={<LazyWrapper><AdminAudit /></LazyWrapper>} />
                  <Route path="traducoes" element={<LazyWrapper><AdminTranslations /></LazyWrapper>} />
                  <Route path="levantamentos" element={<LazyWrapper><AdminWithdrawals /></LazyWrapper>} />
                  <Route path="parceiros" element={<LazyWrapper><AdminPartners /></LazyWrapper>} />
                  <Route path="denuncias" element={<LazyWrapper><AdminReports /></LazyWrapper>} />
                  <Route path="configuracoes" element={<LazyWrapper><AdminSettings /></LazyWrapper>} />
                  <Route path="seguros" element={<LazyWrapper><AdminInsurance /></LazyWrapper>} />
                  <Route path="seguros/seguradoras" element={<LazyWrapper><AdminInsuranceCompanies /></LazyWrapper>} />
                  <Route path="disputas" element={<LazyWrapper><AdminDisputes /></LazyWrapper>} />
                  <Route path="disputas/:id" element={<LazyWrapper><AdminDisputeDetail /></LazyWrapper>} />
                  <Route path="reembolsos" element={<LazyWrapper><AdminRefunds /></LazyWrapper>} />

                </Route>

                
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="/incompativel" element={<LazyWrapper><Incompativel /></LazyWrapper>} />
                <Route path="*" element={<LazyWrapper><NotFound /></LazyWrapper>} />
              </Routes>
            ) : (
              <AnimatePresence mode="wait">
                <Routes location={location} key={location.pathname}>
                  {/* Core routes - eagerly loaded */}
                  {/* Landing Page protected - logged in users are redirected to /explorar */}
                  <Route path="/" element={<PublicOnlyRoute><PageTransition><Index /></PageTransition></PublicOnlyRoute>} />
                  <Route path="/explorar" element={<PageTransition><Explorar /></PageTransition>} />
                  
                  {/* Onboarding - for first-time native app users */}
                  <Route path="/onboarding" element={<PageTransition><LazyWrapper><Onboarding /></LazyWrapper></PageTransition>} />
                  
                  {/* Lazy loaded routes - wrapped with LazyWrapper */}
                  <Route path="/auth" element={<PageTransition><LazyWrapper><Auth /></LazyWrapper></PageTransition>} />
                  <Route path="/imovel/:id" element={<PageTransition><LazyWrapper><PropertyDetails /></LazyWrapper></PageTransition>} />
                  <Route path="/dashboard" element={<Navigate to="/dashboard/anuncios" replace />} />
                  <Route path="/dashboard/anuncios" element={<PageTransition><LazyWrapper><HostProperties /></LazyWrapper></PageTransition>} />
                  <Route path="/dashboard/reservas" element={<PageTransition><LazyWrapper><HostBookings /></LazyWrapper></PageTransition>} />
                  <Route path="/dashboard/calendario" element={<PageTransition><LazyWrapper><HostCalendar /></LazyWrapper></PageTransition>} />
                  <Route path="/dashboard/financeiro" element={<PageTransition><LazyWrapper><HostFinancials /></LazyWrapper></PageTransition>} />
                  <Route path="/anunciar" element={<PageTransition><LazyWrapper><AddProperty /></LazyWrapper></PageTransition>} />
                  <Route path="/favoritos" element={<PageTransition><LazyWrapper><Favoritos /></LazyWrapper></PageTransition>} />
                  <Route path="/perfil" element={<PageTransition><LazyWrapper><Perfil /></LazyWrapper></PageTransition>} />
                  <Route path="/configuracoes" element={<PageTransition><LazyWrapper><Configuracoes /></LazyWrapper></PageTransition>} />
                  <Route path="/suporte" element={<LazyWrapper><Suporte /></LazyWrapper>} />
                  {/* Reservas - URLs modernizadas */}
                  <Route path="/reservas" element={<PageTransition><LazyWrapper><MinhasReservas /></LazyWrapper></PageTransition>} />
                  <Route path="/reservas/:id" element={<PageTransition><LazyWrapper><BookingDetails /></LazyWrapper></PageTransition>} />
                  {/* ReservaConfirmada removed — success is shown inline in PaymentFlow modal */}
                  
                  {/* Ofertas - estrutura hierárquica */}
                  <Route path="/ofertas/nova" element={<PageTransition><LazyWrapper><FazerOferta /></LazyWrapper></PageTransition>} />
                  <Route path="/ofertas/recebidas" element={<PageTransition><LazyWrapper><OfertasRecebidas /></LazyWrapper></PageTransition>} />
                  
                  {/* Redirects para URLs antigas (backward compatibility) */}
                  <Route path="/minhas-reservas" element={<Navigate to="/reservas" replace />} />
                  <Route path="/reserva/:id" element={<ReservaRedirect />} />
                  <Route path="/reserva-confirmada/:id" element={<ReservaRedirect />} />
                  <Route path="/fazer-oferta" element={<Navigate to="/ofertas/nova" replace />} />
                  <Route path="/ofertas-recebidas" element={<Navigate to="/ofertas/recebidas" replace />} />
                  <Route path="/notificacoes" element={<PageTransition><LazyWrapper><Notificacoes /></LazyWrapper></PageTransition>} />

                  {/* Insurance — Owner */}
                  <Route path="/seguro" element={<PageTransition><LazyWrapper><MeuSeguro /></LazyWrapper></PageTransition>} />
                  <Route path="/seguro/imovel/:requestId" element={<PageTransition><LazyWrapper><MeuSeguroDetalhe /></LazyWrapper></PageTransition>} />

                  {/* Insurance — Insurer */}
                  <Route path="/seguradora/login" element={<PageTransition><LazyWrapper><InsurerLogin /></LazyWrapper></PageTransition>} />
                  <Route path="/seguradora" element={<PageTransition><LazyWrapper><InsurerLayout /></LazyWrapper></PageTransition>}>
                    <Route index element={<LazyWrapper><InsurerRequests /></LazyWrapper>} />
                    <Route path="pedidos" element={<LazyWrapper><InsurerRequests /></LazyWrapper>} />
                    <Route path="pedidos/:id" element={<LazyWrapper><InsurerRequestDetail /></LazyWrapper>} />
                  </Route>
                  <Route path="/termos" element={<PageTransition><LazyWrapper><Termos /></LazyWrapper></PageTransition>} />
                  <Route path="/opcoes-de-cancelamento" element={<PageTransition><LazyWrapper><OpcoesCancelamento /></LazyWrapper></PageTransition>} />
                  <Route path="/ajuda" element={<PageTransition><LazyWrapper><Ajuda /></LazyWrapper></PageTransition>} />
                  <Route path="/privacidade" element={<PageTransition><LazyWrapper><Privacidade /></LazyWrapper></PageTransition>} />
                
                  {/* Affiliate Routes - lazy loaded */}
                  <Route path="/afiliados" element={<LazyWrapper><AfiliadoAuth /></LazyWrapper>} />
                  <Route path="/afiliados/painel" element={<LazyWrapper><AfiliatePanel /></LazyWrapper>}>
                    <Route index element={<LazyWrapper><AffiliateDashboard /></LazyWrapper>} />
                    <Route path="explorar" element={<LazyWrapper><AffiliateExplore /></LazyWrapper>} />
                    <Route path="reservas" element={<LazyWrapper><AffiliateReservations /></LazyWrapper>} />
                    <Route path="transacoes" element={<LazyWrapper><AffiliateTransactions /></LazyWrapper>} />
                    <Route path="saldo" element={<LazyWrapper><AffiliateBalance /></LazyWrapper>} />
                    <Route path="notificacoes" element={<LazyWrapper><AffiliateNotifications /></LazyWrapper>} />
                    <Route path="perfil" element={<LazyWrapper><AffiliateProfile /></LazyWrapper>} />
                  </Route>
                  <Route path="/ref/:code" element={<LazyWrapper><AffiliateRedirect /></LazyWrapper>} />
                  
                  {/* Admin Routes - lazy loaded */}
                  <Route path="/admin/login" element={<LazyWrapper><AdminLogin /></LazyWrapper>} />
                  <Route path="/admin" element={<LazyWrapper><AdminLayout /></LazyWrapper>}>
                    <Route index element={<LazyWrapper><AdminDashboard /></LazyWrapper>} />
                    <Route path="utilizadores" element={<LazyWrapper><AdminUsers /></LazyWrapper>} />
                    <Route path="imoveis" element={<LazyWrapper><AdminProperties /></LazyWrapper>} />
                    <Route path="kyc" element={<LazyWrapper><AdminKYC /></LazyWrapper>} />
                    <Route path="reservas" element={<LazyWrapper><AdminBookings /></LazyWrapper>} />
                    <Route path="financeiro" element={<LazyWrapper><AdminFinancials /></LazyWrapper>} />
                    <Route path="pagamentos" element={<LazyWrapper><AdminPayments /></LazyWrapper>} />
                    <Route path="transacoes" element={<LazyWrapper><AdminTransactions /></LazyWrapper>} />
                    <Route path="afiliados" element={<LazyWrapper><AdminAffiliates /></LazyWrapper>} />
                    <Route path="ofertas" element={<LazyWrapper><AdminOffers /></LazyWrapper>} />
                    <Route path="avaliacoes" element={<LazyWrapper><AdminReviews /></LazyWrapper>} />
                    <Route path="chat" element={<LazyWrapper><AdminChat /></LazyWrapper>} />
                    <Route path="notificacoes" element={<LazyWrapper><AdminNotifications /></LazyWrapper>} />
                    <Route path="auditoria" element={<LazyWrapper><AdminAudit /></LazyWrapper>} />
                    <Route path="traducoes" element={<LazyWrapper><AdminTranslations /></LazyWrapper>} />
                    <Route path="levantamentos" element={<LazyWrapper><AdminWithdrawals /></LazyWrapper>} />
                    <Route path="parceiros" element={<LazyWrapper><AdminPartners /></LazyWrapper>} />
                    <Route path="denuncias" element={<LazyWrapper><AdminReports /></LazyWrapper>} />
                    <Route path="configuracoes" element={<LazyWrapper><AdminSettings /></LazyWrapper>} />
                    <Route path="seguros" element={<LazyWrapper><AdminInsurance /></LazyWrapper>} />
                    <Route path="seguros/seguradoras" element={<LazyWrapper><AdminInsuranceCompanies /></LazyWrapper>} />
                    <Route path="bera-ia" element={<LazyWrapper><AdminBeraAI /></LazyWrapper>} />
                    <Route path="disputas" element={<LazyWrapper><AdminDisputes /></LazyWrapper>} />
                    <Route path="disputas/:id" element={<LazyWrapper><AdminDisputeDetail /></LazyWrapper>} />
                    <Route path="reembolsos" element={<LazyWrapper><AdminRefunds /></LazyWrapper>} />

                  </Route>

                  
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="/incompativel" element={<LazyWrapper><Incompativel /></LazyWrapper>} />
                  <Route path="*" element={<LazyWrapper><NotFound /></LazyWrapper>} />
                </Routes>
              </AnimatePresence>
            )}
            {/* Hide bottom tabs on onboarding, auth, and admin routes */}
            {showBottomTabs && (
              <MobileBottomTabs onMenuOpen={() => window.dispatchEvent(new CustomEvent('open-mobile-menu'))} />
            )}
            <InstallPWAPrompt />
            <UpdateBanner />
            <BeraIAWidget />
            <TwoFactorGate />
            <MaintenanceGate />
            <PostLoginVerifyGate />
            <GlobalPaymentConfirmedGate />

          </>
        )}
      </div>
    </AuthProvider>
  );
};

// Inner component with all hooks - inside all providers
const AppWithHooks = () => {
  log('=== AppWithHooks Rendering ===');
  
  // Safe hooks - now inside QueryClientProvider
  useGlobalGeolocation();
  // Unified realtime + focus/reconnect sync across desktop, mobile web, Android, iOS, tablet
  useUnifiedSync(undefined);
  
  // Offline hook
  const { online } = useOffline();
  
  return <AppContent />;
};

const App = () => {
  log('=== App Component Rendering ===');
  
  // State for safe initialization - outside providers
  const [isMounted, setIsMounted] = useState(false);
  const [appReady, setAppReady] = useState(false);
  const [initError, setInitError] = useState<Error | null>(null);
  
  // Safe mount check for Capacitor WebView
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsMounted(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  log('App component initializing... Capacitor native:', isCapacitorNative());

  // Register service worker and check cache version - deferred for Chrome performance
  useEffect(() => {
    if (!isMounted) return;

    // Defer SW registration to not block rendering (Chrome optimization)
    const initTimer = setTimeout(() => {
      try {
        log('Registering service worker...');
        // Expose window.__resetCache() for manual debugging
        exposeCacheResetGlobal();
        // Non-blocking SW registration
        registerSW().catch((e) => {
          console.warn('[BeraStays] SW registration failed:', e);
        });

        // Defer cache check as well
        setTimeout(() => {
          try {
            checkAndClearCache();
          } catch (e) {
            console.warn('[BeraStays] Cache check error:', e);
          }
        }, 500);

        log('App ready!');
      } catch (e) {
        console.error('[BeraStays] Init error:', e);
      }
    }, 100);

    // Always mark app ready immediately to prevent blocking
    setAppReady(true);

    return () => clearTimeout(initTimer);
  }, [isMounted]);
  
  // Remove loader
  useEffect(() => {
    if (appReady && typeof document !== 'undefined') {
      const loader = document.getElementById('app-loader');
      if (loader) {
        loader.classList.add('hidden');
        setTimeout(() => loader.remove(), 300);
      }
      document.body.classList.add('app-loaded');
    }
  }, [appReady]);

  // Preload Google Maps key + warm tiles in idle for instant map opening
  useEffect(() => {
    if (!isMounted) return;

    log('Preloading Google Maps key...');
    preloadMapboxToken().then((token) => {
      if (token) {
        log('Google Maps key preloaded');
        const warm = () => {
          import('@/lib/warmupMapbox').then(m => m.warmupMapbox()).catch(() => {});
        };
        if ('requestIdleCallback' in window) {
          (window as any).requestIdleCallback(warm, { timeout: 5000 });
        } else {
          setTimeout(warm, 2500);
        }
      }
    }).catch((err) => {
      console.warn('[BeraStays] Google Maps preload error:', err);
    });
  }, [isMounted]);


  // Listen for sync events
  useEffect(() => {
    if (!isMounted || typeof window === 'undefined') return;
    
    const handleSync = () => {
      queryClient.invalidateQueries();
    };
    
    window.addEventListener('sw-sync-bookings', handleSync);
    window.addEventListener('online', handleSync);
    
    return () => {
      window.removeEventListener('sw-sync-bookings', handleSync);
      window.removeEventListener('online', handleSync);
    };
  }, [isMounted]);

  if (!appReady) {
    return (
      <div className="fixed inset-0 bg-[#FAF9F7] flex items-center justify-center">
        <div className="text-foreground text-lg animate-pulse">{typeof window !== "undefined" && window.localStorage.getItem("i18nextLng")?.startsWith("en") ? "Loading..." : "Carregando..."}</div>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="fixed inset-0 bg-red-50 flex flex-col items-center justify-center p-4">
        <h1 className="text-xl font-bold text-red-600 mb-2">{typeof window !== "undefined" && window.localStorage.getItem("i18nextLng")?.startsWith("en") ? "Initialization Error" : "Erro de Inicialização"}</h1>
        <p className="text-sm text-red-500 mb-4 text-center font-mono">
          {initError.message}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-red-600 text-white rounded-lg"
        >
          {typeof window !== "undefined" && window.localStorage.getItem("i18nextLng")?.startsWith("en") ? "Try Again" : "Tentar Novamente"}
        </button>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="berastays-theme">
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <OfflineIndicator />
              <UpdatePrompt />
              <SyncStatus pending={0} />
              <TrackingPixels />
              <HashRouter>
                <AppWithHooks />
              </HashRouter>
            </TooltipProvider>
          </ThemeProvider>
        </HelmetProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
